import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Link as MuiLink,
  DialogContent,
  Grid,
} from '@material-ui/core';
import { ApplicationState } from 'store';
import { useTranslation } from 'react-i18next';
import { SnackbarComponent } from 'components/SnackbarComponent';

import 'translations/translations';
import { Link } from 'react-router-dom';
import OverlaySpinner from 'components/spinners/OverlaySpinner';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  title: string;
  children: React.ReactNode;
  snackLinkText?: string;
  snackLink?: string;
  message: string;
  fullWidth: boolean;
  contentHeight?: string;
};

const useStyles = makeStyles((theme) => ({
  alignCenter: {
    textAlign: 'center',
  },
  snackLinkStyle: {
    color: '#fff',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  noMarginBottom: {
    marginBottom: 0,
  },
  lastButton: {
    marginRight: 0,
    marginBottom: 0,
  },
}));

const FlowDialog: React.FC<DialogProps> = (props) => {
  const {
    children,
    handleClose,
    isOpen,
    title,
    snackLinkText,
    snackLink,
    message,
    fullWidth,
    contentHeight,
  } = props;
  const [snackIsOpen, setSnackOpen] = useState(false);
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const classes = useStyles();
  const childrenArr = React.Children.toArray(children);

  const dialogHeight = contentHeight || '10rem';
  const style = {
    height: `${dialogHeight}`,
  };

  const isProcessing = useSelector(
    (state: ApplicationState) => state.flowDialog?.isProcessing,
  );

  const stepToShow = useSelector(
    (state: ApplicationState) => state.flowDialog?.step,
  );

  const callbackFn = useSelector(
    (state: ApplicationState) => state.flowDialog?.callbackFn,
  );

  const isButtonDisabled = useSelector(
    (state: ApplicationState) => state.flowDialog?.buttonDisabled,
  );

  const isCompleted = useSelector(
    (state: ApplicationState) => state.flowDialog?.isCompleted,
  );

  const handleCancel = () => {
    handleClose();
    dispatch({ type: 'SET_STEP', step: 1 });
  };

  const nextStepFn = () => {
    if (callbackFn) {
      dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });
      callbackFn();
    }
  };

  const handleCloseSnack = () => {
    setSnackOpen(false);
  };

  useEffect(() => {
    if (isCompleted) {
      setSnackOpen(true);
      handleClose();
    } else {
      setSnackOpen(false);
    }
  }, [isCompleted]);

  useEffect(() => {
    dispatch({ type: 'CLEAR_FLOW_COMPLETED' });
  }, []);

  return (
    <>
      <Dialog
        fullWidth={fullWidth}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        maxWidth="md"
        open={isOpen}
        onClose={handleClose}
      >
        <OverlaySpinner isLoading={isProcessing}>
          <DialogTitle disableTypography={true}>
            <Typography variant="h3">{t(title)}</Typography>
            <Stepper activeStep={stepToShow - 1} alternativeLabel>
              {childrenArr.map((child: React.ReactNode, index) => {
                const title =
                  React.isValidElement(child) && child?.props.stepTitleString;
                const stepProps: { completed?: boolean } = {};

                return (
                  <Step key={index} {...stepProps}>
                    <StepLabel>{t(title)}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </DialogTitle>
          {childrenArr.map((child: React.ReactNode, index) => {
            if (React.isValidElement(child) && stepToShow === index + 1) {
              return (
                <React.Fragment key={index}>
                  <DialogContent style={style}>
                    <Grid container spacing={2}>
                      {child}
                    </Grid>
                  </DialogContent>

                  <DialogActions>
                    <Button
                      onClick={handleCancel}
                      color="secondary"
                      className={classes.noMarginBottom}
                    >
                      {t('common.close')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => nextStepFn()}
                      disabled={isButtonDisabled}
                      className={classes.lastButton}
                    >
                      {t(child.props.buttonText)}
                    </Button>
                  </DialogActions>
                </React.Fragment>
              );
            }
            return null;
          })}
        </OverlaySpinner>
      </Dialog>

      <SnackbarComponent
        variant="success"
        isOpen={snackIsOpen}
        handleClose={handleCloseSnack}
        message={message}
        action={
          <React.Fragment>
            {snackLink && (
              <MuiLink
                className={classes.snackLinkStyle}
                component={Link}
                to={{
                  pathname: `${snackLink}`,
                }}
              >
                {snackLinkText}
              </MuiLink>
            )}
          </React.Fragment>
        }
      />
    </>
  );
};

export { FlowDialog };
